import { IPolicy, IPolicyDto, IPolicyTranslation } from '@app/interfaces/policy.interface';
import { GenericModel } from '@app/models/generic.model';
import { TPolicyType } from '@app/types/policy.type';
import { POLICY_SCOPE_ALL } from '@app/utils/constants';

export class PolicyModel extends GenericModel<IPolicy, IPolicyDto> implements IPolicy {
    id: string;
    scope: string;
    type: TPolicyType;
    version: string;
    requireUserAgreement: boolean;
    published: boolean;
    translations: IPolicyTranslation[];

    constructor(data?: Partial<IPolicyDto>) {
        super(data);

        this.id = data?.id ?? '';
        this.scope = data?.scope ?? POLICY_SCOPE_ALL;
        this.type = data?.type ?? 'TERMS_AND_CONDITIONS';
        this.version = data?.version ?? '';
        this.requireUserAgreement = data?.requireUserAgreement ?? true;
        this.published = data?.published ?? false;
        this.translations = (data?.translations ?? []).map(translation => ({ ...translation }));
    }

    get languages(): string {
        return this.translations.map(translation => translation.language).join(', ');
    }

    getContent(language: string): string {
        return (
            this.translations.find(translation => translation.language === language)?.content ?? ''
        );
    }

    dataTransferObject(): Partial<IPolicyDto> {
        return {
            scope: this.scope,
            type: this.type,
            version: this.version,
            requireUserAgreement: this.requireUserAgreement,
            translations: this.translations,
        };
    }

    /**
     * Only what changed since the policy was loaded (type and scope are immutable). PATCH replaces
     * every field it receives, so a settings save must not carry translations a content save is changing.
     */
    patchDataTransferObject(): Partial<IPolicyDto> {
        const original = this.getOriginalData();
        const dto: Partial<IPolicyDto> = {};
        if (this.version !== original.version) dto.version = this.version;
        if (this.requireUserAgreement !== original.requireUserAgreement) {
            dto.requireUserAgreement = this.requireUserAgreement;
        }
        if (JSON.stringify(this.translations) !== JSON.stringify(original.translations ?? [])) {
            dto.translations = this.translations;
        }
        return dto;
    }
}
